import { CentricTypography as Typography } from '@core/component/CentricTypography'
import { ClearIcon, SearchIcon } from '@core/icons'
import {
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  makeStyles
} from '@material-ui/core'
import map from 'lodash/map'
import { useObserver } from 'mobx-react-lite'
import React from 'react'
import { useTranslation } from 'react-i18next'
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import { SORT_ORDERS } from './sort_constants'

const useStyles = makeStyles(theme => ({
  sortContainer: {
    height: '100%',
    alignItems: 'flex-start',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default
  },
  headerSort: {
    height: 60,
    padding: '25px 25px 5px 25px',
    display: 'flex',
    justifyContent: 'space-between'
  },
  searchContainer: {
    height: 40,
    padding: '0px 25px 10px 25px'
  },
  searchIcon: {
    fontSize: 24,
    margin: '0 0 8px 0px'
  },
  inputRoot: {
    color: 'inherit',
    width: '100%',
    '& .MuiInputBase-input': {
      padding: '6px 0 3px'
    }
  },
  input: {
    '&::placeholder': {
      fontSize: 12,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontStyle: 'normal'
    }
  },
  inputBox: {
    transition: theme.transitions.create('width'),
    width: '100%'
  },
  sortBody: ({ isSearchBoxVisible }: any) => ({
    backgroundColor: '#ffffff',
    padding: '10px 25px 10px 25px',
    height: isSearchBoxVisible ? 'calc(100% - 100px)' : 'calc(100% - 60px)',
    overflow: 'auto',
    flexDirection: 'column',
    flexWrap: 'nowrap'
  }),
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
    height: '40px',
    '& .MuiFormControlLabel-root': {
      marginRight: 6
    }
  },
  sortFormControl: {
    width: '100%'
  },
  sortFormControlLabel: {
    maxWidth: 'calc(90% - 12px)',
    marginRight: 12,
    marginLeft: 0
  },
  radioBtn: {
    display: 'flex',
    height: '24px',
    width: '24px',
    padding: '4.3px 2.96px 4.3px 2.961px',
    justifyContent: 'center',
    alignItems: 'center'
  },
  arrowIcons: {
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer'
  },
  clickArrow: {
    color: 'rgb(68, 68, 68)',
    height: '8px'
  },
  notClickArrow: {
    color: 'rgb(204, 204, 204)',
    height: '8px'
  },
  hiddenBtn: {
    width: 0,
    height: 0,
    padding: 0,
    display: 'none'
  },
  infoMessage: {
    justifyContent: 'center',
    margin: '40px 0px',
    '& h6': {
      textTransform: 'initial',
      textAlign: 'center'
    }
  },
  clearIcon: {
    cursor: 'pointer'
  },
  sortLabel: {
    paddingLeft: 8
  }
}))

export const EnhancedSortContent = ({
  enhancedSortStore,
  applySortByOptions,
  onClose
}) => {
  return useObserver(function useHook() {
    const {
      selectedSortOption,
      filteredSortAttributesList,
      sortAttributeList,
      showSortSearchInput,
      sortSearchText,
      updateSortSearchText
    } = enhancedSortStore
    const { t } = useTranslation()
    const isSearchBoxVisible = showSortSearchInput && sortAttributeList.length
    const classes = useStyles({ isSearchBoxVisible })

    return (
      <Grid container className={classes.sortContainer}>
        <Grid item xs={12} className={classes.headerSort}>
          <Typography variant='h4'>{t('SA.enhancedView.sortBy', 'Sort By')}</Typography>
          <div className={classes.clearIcon} onClick={onClose}>
            <ClearIcon />
          </div>
          {/* Added this hidden button to avoid unnecesary focus on radio button */}
          <Button className={classes.hiddenBtn} />
        </Grid>
        {isSearchBoxVisible ? (
          <Grid item xs={12} className={classes.searchContainer}>
            <TextField
              value={sortSearchText}
              onChange={e => updateSortSearchText(e.target.value)}
              className={classes.inputRoot}
              classes={{
                root: classes.inputBox
              }}
              variant='standard'
              placeholder={t('SA.enhancedView.searchSort', 'Search sorting')}
              size='small'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon className={classes.searchIcon} />
                  </InputAdornment>
                ),
                classes: { input: classes.input }
              }}
            />
          </Grid>
        ) : null}

        <Grid container direction='row' className={classes.sortBody}>
          {!sortAttributeList.length ? (
            <Grid container className={classes.infoMessage}>
              <Typography variant={'subtitle1Grey'}>
                {t(
                  'bb.settings.productCardConfigSettings.labels.noSortAttributeNote',
                  'No any attribute is configured for sorting, Please connect with admin to configure attributes for sorting'
                )}
              </Typography>
            </Grid>
          ) : null}
          {filteredSortAttributesList.length ? (
            map(filteredSortAttributesList, option => {
              const isSelected = selectedSortOption?.id === option.id
              return (
                <FormControl key={option.id} className={classes.sortFormControl}>
                  <RadioGroup
                    className={classes.radioGroup}
                    onChange={() => applySortByOptions(option.id)}
                  >
                    <FormControlLabel
                      className={classes.sortFormControlLabel}
                      value={option.id}
                      control={
                        <Radio
                          className={classes.radioBtn}
                          checked={isSelected}
                          color='default'
                          size='small'
                        />
                      }
                      label={
                        <div
                          className={classes.sortLabel}
                          onClick={() => {
                            selectedSortOption.id === option.id &&
                            selectedSortOption.orderBy === SORT_ORDERS.ASC
                              ? applySortByOptions(option.id, SORT_ORDERS.DESC)
                              : applySortByOptions(option.id, SORT_ORDERS.ASC)
                          }}
                        >
                          <Typography
                            variant={isSelected ? 'caption' : 'captionDarkGrey'}
                          >
                            {t(option.labelKey, option.displayName)}
                          </Typography>
                        </div>
                      }
                    />
                    {isSelected ? (
                      <Grid
                        className={classes.arrowIcons}
                        onClick={() => {
                          selectedSortOption.orderBy === SORT_ORDERS.ASC
                            ? applySortByOptions(option.id, SORT_ORDERS.DESC)
                            : applySortByOptions(option.id, SORT_ORDERS.ASC)
                        }}
                      >
                        <Grid
                          className={
                            selectedSortOption?.orderBy === SORT_ORDERS.ASC
                              ? classes.clickArrow
                              : classes.notClickArrow
                          }
                        >
                          <ArrowDropUpIcon fontSize='small' />
                        </Grid>
                        <Grid
                          className={
                            selectedSortOption?.orderBy === SORT_ORDERS.DESC
                              ? classes.clickArrow
                              : classes.notClickArrow
                          }
                        >
                          <ArrowDropDownIcon fontSize='small' />
                        </Grid>
                      </Grid>
                    ) : null}
                  </RadioGroup>
                </FormControl>
              )
            })
          ) : sortAttributeList.length ? (
            <Grid container className={classes.infoMessage}>
              <Typography variant={'subtitle1Grey'}>
                {t(
                  'bb.settings.productCardConfigSettings.labels.noMatchFound',
                  'No attribute found for this search criteria'
                )}
              </Typography>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    )
  })
}
